import { Button, Col, DatePicker, Form, message, Modal, Radio, Row, Select, Table, TableProps, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import SelectEmployee from "../../../components/form/SelectEmployee";
import { generateFormRules } from "../../../helpers/formRules";
import { EEmployeeRole } from "../../../types/employee.type";
import { UserProperties, UserPropertiesWithTL } from "../../../types/user.type";
import { EFeeFor, EFeeFrequency, EFeePosition, EFeeStatus, EFeeType } from "../../../types/masterFee.type";
import {
	generateNextFeeCharged,
	renderFeeDateRange,
	renderFeeFor,
	renderFeeFrequency,
	renderFeeType,
} from "../../../helpers/fee";
import InputPercentOrAmout from "../../../components/form/InputPercentOrAmount";
import { floatToPercentageWithFixed, percentageToFloatWithFixed } from "../../../helpers/number";
import { renderEmployeeCodeOrTemp } from "../../../helpers/employee";
import { ListItem } from "./GroupInformation";
import { getErrorMessage } from "../../../helpers/errorHandler";
import { httpRequest } from "../../../helpers/api";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { priceFormat } from "../../../helpers/priceFormat";
import { BaseResponsePaginationProps } from "../../../types/config.type";
import { TeamsMemberFeeItemProperties } from "../../../types/teamsMemberFee.type";
import { checkDateInRange } from "../../../helpers/format";

const { RangePicker } = DatePicker;

interface AddMemberProps {
	officeId?: string | string[];
	excludeUserId?: string[];
	open?: boolean;
	onClose?: () => void;
	onSelect?: (user: UserPropertiesWithTL) => void;
	selectedMember?: ListItem;
	isEdit?: boolean;
	isDetail?: boolean;
	resetListFee?: boolean;
}

const AddMember: React.FC<AddMemberProps> = ({
	open,
	onClose,
	onSelect,
	excludeUserId,
	officeId,
	selectedMember,
	isEdit,
	isDetail,
	resetListFee = true,
}) => {
	const [form] = Form.useForm();
	const [innerForm] = Form.useForm();
	const [agentList, setAgentList] = useState<UserProperties[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [innerOpen, setInnerOpen] = useState(false);
	const [listFee, setListFee] = useState<TeamsMemberFeeItemProperties[]>([]);
	const [deletedFees, setDeletedFees] = useState<TeamsMemberFeeItemProperties[]>([]);

	const _handleSelect = async (values: { userId: string }) => {
		if (listFee.length === 0) {
			message.error("Fee can't be empty!");
			return;
		}

		if (selectedMember) {
			if (!selectedMember) return message.error("Something went wrong, can't get employee data!");
			if (isEdit && selectedMember.groupMemberId) {
				console.log("selectedMember", selectedMember);
				try {
					setIsLoading(true);
					const newFees = listFee?.filter((row) => row.isNew);
					let updatedFeeIds = listFee
						.filter((item) => item.teamsMemberFeeItemId)
						.map((item) => item.teamsMemberFeeItemId);
					let deletedFees = (selectedMember?.user?.teamsMemberFees ?? [])!.filter(
						(item) => !updatedFeeIds.includes(item.teamsMemberFeeItemId)
					);

					let promises: Promise<unknown>[] = [];
					for (const fee of newFees) {
						let dtoFee = {
							teamsMemberFeeId: selectedMember?.user?.teamsMemberFees?.[0].teamsMemberFeeId,
							position: EFeePosition.BOTTOM,
							type: fee.type,
							frequency: fee.frequency,
							feeFor: fee.feeFor,
							value: fee.value,
							nextChargeAt: fee.nextChargeAt,
							startAt: fee.startAt,
							endAt: fee.endAt,
							status: EFeeStatus.PUBLISH,
						};
						promises.push(httpRequest.post("teams-member-fee-items", dtoFee));
					}
					for (const fee of deletedFees) {
						promises.push(httpRequest.delete(`teams-member-fee-items/${fee.teamsMemberFeeItemId}`));
					}
					await Promise.all(promises);
					const memberFees = await httpRequest.get<BaseResponsePaginationProps<TeamsMemberFeeItemProperties>>(
						`teams-member-fee-items?filterTeamsMemberFeeId=${selectedMember?.user?.teamsMemberFees?.[0]?.teamsMemberFeeId}`
					);

					onSelect?.({
						...selectedMember.user,
						teamsMemberFees: memberFees.data.payload.results.map((item) => ({
							...item,
							isNew: false,
						})),
						deletedFees: deletedFees,
					});
					// onUpdate?.();
					message.success("Success update commission");
				} catch (error) {
					message.error(getErrorMessage(error));
				} finally {
					setIsLoading(false);
				}
			} else {
				onSelect?.({
					...selectedMember.user,
					teamsMemberFees: listFee,
					deletedFees: deletedFees,
				});
			}
		} else {
			let selected = agentList.find((item) => item.userId === values.userId);
			if (!selected) return message.error("Something went wrong, can't get employee data! 3");

			onSelect?.({
				...selected,
				teamsMemberFees: listFee,
				deletedFees: deletedFees,
			});
		}

		handleCancel();
	};

	const typeWatch = Form.useWatch("type", innerForm);

	const feeLabel = useMemo(() => {
		return typeWatch === EFeeType.AMOUNT ? "Nominal (Rp)" : "Percentage (%)";
	}, [typeWatch]);

	const FREQUENCY_OPTIONS = (type: EFeeType) => {
		if (type === EFeeType.PERCENTAGE) {
			return [
				{
					label: renderFeeFrequency(EFeeFrequency.PER_TRANSACTION),
					value: EFeeFrequency.PER_TRANSACTION,
				},
			];
		} else {
			return Object.values(EFeeFrequency)
				.filter((v) => ![EFeeFrequency.ONE_TIME].includes(v))
				.map((value) => ({
					label: renderFeeFrequency(value as EFeeFrequency),
					value,
				}));
		}
	};

	useEffect(() => {
		if (selectedMember) {
			form.setFieldsValue({
				userId: selectedMember.user.userId,
			});
			setListFee(selectedMember.user.teamsMemberFees || []);
		}
	}, [selectedMember]);

	const COLUMNS = [
		{
			title: "FEES",
			key: "feeFor",
			dataIndex: "feeFor",
			render: (value: EFeeFor) => <>{renderFeeFor(value)}</>,
			width: "20%",
		},
		{
			title: "COMMISSION",
			key: "value",
			dataIndex: "value",
			render: (value: number, record: TeamsMemberFeeItemProperties) => (
				<>
					{record.type === EFeeType.AMOUNT
						? `Rp ${priceFormat(value)}`
						: floatToPercentageWithFixed(value) + "%"}
				</>
			),
			width: "20%",
		},

		{
			title: "FREQUENCY",
			key: "frequency",
			dataIndex: "frequency",
			render: (value: EFeeFrequency) => <>{renderFeeFrequency(value)}</>,
			width: isDetail ? "30%" : "20%",
		},
		{
			title: "DATE RANGE",
			key: "date",
			dataIndex: "date",
			render: (value: string, record: TeamsMemberFeeItemProperties) => {
				const isAvailable = checkDateInRange(record?.startAt!, record.endAt!);

				return (
					<Typography.Text
						style={{
							color: isAvailable ? "#389e0d" : "red",
						}}
					>
						{renderFeeDateRange(record.startAt, record.endAt)}
					</Typography.Text>
				);
			},
			width: "30%",
		},
		!isDetail
			? {
					title: "ACTION",
					key: "action",
					dataIndex: "action",
					render: (_: any, record: TeamsMemberFeeItemProperties) => {
						return (
							<Button
								danger={true}
								type="primary"
								icon={<DeleteOutlined />}
								onClick={() => {
									const newData = listFee.filter(
										(v) => v.teamsMemberFeeItemId !== record.teamsMemberFeeItemId
									);

									setListFee(newData);
									if (!record.isNew) {
										setDeletedFees([...deletedFees, record]);
									}
								}}
							/>
						);
					},
					width: "10%",
			  }
			: null,
	].filter(Boolean) as TableProps<any>["columns"];

	const handleSubmitFee = () => {
		const values = innerForm.getFieldsValue();

		const teamsMemberFeeItemId = Date.now().toString(36) + Math.random().toString(36).substr(2);

		const commission =
			values.type === EFeeType.AMOUNT ? values.value : percentageToFloatWithFixed(values.value) ?? 0;
		const data = {
			...values,
			value: commission,
			nextChargeAt: values.dateRange?.[0]
				? generateNextFeeCharged(values.dateRange[0].clone().startOf("day"), values.frequency)
				: null,
			startAt: values.dateRange?.[0] ? values.dateRange[0].startOf("day") : null,
			endAt: values.dateRange?.[1] ? values.dateRange[1].endOf("day") : null,
			isNew: true,
			teamsMemberFeeItemId,
		};

		setListFee([...listFee, data]);
		setInnerOpen(false);
		innerForm.resetFields();
	};

	const isMaxAddFee = useMemo(() => {
		const findAmount = listFee.some((v) => v.type === EFeeType.AMOUNT);
		const findPercentage = listFee.some((v) => v.type === EFeeType.PERCENTAGE);
		if (findAmount && findPercentage) {
			return true;
		} else {
			return false;
		}
	}, [listFee]);

	const initialTypeValue = useMemo(() => {
		const findAmount = listFee.some((v) => v.type === EFeeType.AMOUNT);
		const findPercentage = listFee.some((v) => v.type === EFeeType.PERCENTAGE);
		if (!findAmount && !findPercentage) {
			return EFeeType.PERCENTAGE;
		} else if (findAmount) {
			return EFeeType.PERCENTAGE;
		} else if (findPercentage) {
			return EFeeType.AMOUNT;
		}
	}, [listFee]);

	const handleCancel = () => {
		if (isDetail) {
			onClose?.();
		} else {
			onClose?.();
			if (resetListFee) {
				setListFee([]);
			}
			form.resetFields();
		}
	};

	return (
		<Modal
			open={open}
			onCancel={handleCancel}
			cancelButtonProps={{ style: { display: isDetail ? "none" : "initial" } }}
			title={`${isDetail ? "Detail Fees" : selectedMember ? "Update Fee" : "Add Member"}`}
			destroyOnClose={true}
			okText={isDetail ? "Close" : "Ok"}
			onOk={form.submit}
			okButtonProps={{
				loading: isLoading,
			}}
			width={768}
		>
			<Form
				form={form}
				layout="vertical"
				preserve={false}
				onFinish={_handleSelect}
			>
				<Form.Item
					label="Agent"
					name="userId"
					rules={selectedMember ? undefined : generateFormRules("Agent", ["required"])}
					className={selectedMember ? "reset-mb-ant-form-item-label" : ""}
				>
					{selectedMember ? (
						<Typography.Text
							style={{
								fontSize: 18,
								fontWeight: "bold",
							}}
						>
							{`${selectedMember.user.nickName?.toUpperCase()} / ${selectedMember.user.employee?.[0]?.office?.officeName?.toUpperCase()} / ${renderEmployeeCodeOrTemp(
								selectedMember.user.employee?.[0]
							)}`}
						</Typography.Text>
					) : (
						<SelectEmployee
							officeId={officeId}
							userList={agentList}
							setUserList={setAgentList}
							customQuery={{
								filterRole: EEmployeeRole.agent,
								includeData: "listingCount,profile,employeeOffice",
								isAggregateSingleOffice: "true",
							}}
							excludeUserId={excludeUserId}
							placeholder="Choose agent"
							isLabelWithOffice
						/>
					)}
				</Form.Item>

				{!isDetail && (
					<Row
						style={{
							margin: "20px 0px",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<Typography.Title level={4}>List Fee</Typography.Title>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => {
								setInnerOpen(true);
								innerForm.setFieldValue("type", EFeeType.PERCENTAGE);
								innerForm.setFieldValue("feeFor", EFeeFor.AGENT);
							}}
							// disabled={isMaxAddFee}
						>
							Add Fee
						</Button>
					</Row>
				)}

				<Table
					columns={COLUMNS}
					pagination={false}
					dataSource={listFee}
					scroll={{
						x: "100%",
					}}
				/>
			</Form>

			<Modal
				open={innerOpen}
				onCancel={() => {
					setInnerOpen(false);
					innerForm.resetFields();
				}}
				title="Add Fee"
				destroyOnClose={true}
				onOk={innerForm.submit}
				width={568}
			>
				<Form
					form={innerForm}
					layout="vertical"
					onFinish={handleSubmitFee}
				>
					<Row gutter={[24, 12]}>
						<Col span={12}>
							<Form.Item
								label="Fees"
								name="feeFor"
								rules={generateFormRules("Fees", ["required"])}
							>
								<Radio.Group
									options={Object.values(EFeeFor).map((value) => ({
										label: renderFeeFor(value as EFeeFor),
										value,
									}))}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								label="Type Fee"
								name="type"
								rules={generateFormRules("Type Fee", ["required"])}
							>
								<Radio.Group
									options={Object.values(EFeeType).map((value) => ({
										label: renderFeeType(value as EFeeType),
										value,
										// disabled: listFee.some((v) => v.type === value),
									}))}
									onChange={() => {
										innerForm.setFieldsValue({
											value: undefined,
											frequency: undefined,
										});
									}}
								/>
							</Form.Item>
						</Col>
						<Col
							span={24}
							md={12}
						>
							<Form.Item
								label={feeLabel}
								name="value"
								rules={generateFormRules("Value", ["required"])}
							>
								<InputPercentOrAmout
									inputFormat={typeWatch}
									placeholder={
										typeWatch === EFeeType.PERCENTAGE ? "Input percentage" : "Input nominal"
									}
								/>
							</Form.Item>
						</Col>
						<Col
							span={24}
							md={12}
						>
							<Form.Item
								label="Frequency"
								name="frequency"
								rules={generateFormRules("Frequency", ["required"])}
							>
								<Select
									virtual={true}
									placeholder="Choose frequency"
									options={FREQUENCY_OPTIONS(typeWatch)}
								/>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item
								label="Date Range"
								name="dateRange"
								rules={generateFormRules("Start Date", ["required", "required-start-date"])}
							>
								<RangePicker
									allowEmpty={[false, true]}
									style={{ width: "100%" }}
									format="DD MMM YYYY"
								/>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Typography.Text strong={true}>Note</Typography.Text>
							<br />
							<Typography.Text type="secondary">Keep end date empty to make fee infinity</Typography.Text>
							<br />
							<br />
						</Col>
					</Row>
				</Form>
			</Modal>
		</Modal>
	);
};
export default AddMember;
